import React from 'react';
import styles from './Plot.module.css';
import * as Simulator from '../../../utils/simulator';

import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';

import Plot from 'react-plotly.js';

const tracesTemplate = [

    {name: 'y', marker: {color: 'blue'}, yaxis: 'y'},
    {name: 'r', marker: {color: 'green'}, yaxis: 'y'},

    {name: 'u_sat', marker: {color: 'red'}, yaxis: 'y2'},
    {name: 'u', marker: {color: 'orange'}, line: {dash: 'dash'}, yaxis: 'y2'},
    {name: 'u_min', marker: {color: 'black'}, line: {dash: 'dash'}, opacity: 0.5, yaxis: 'y2'},
    {name: 'u_max', marker: {color: 'black'}, line: {dash: 'dash'}, opacity: 0.5, yaxis: 'y2'},
    {name: 'u_lower', marker: {color: 'black'}, line: {dash: 'dashdot'}, opacity: 0.5, yaxis: 'y2'},
    {name: 'u_middle', marker: {color: 'black'}, line: {dash: 'dashdot'}, opacity: 0.5, yaxis: 'y2'},
    {name: 'u_upper', marker: {color: 'black'}, line: {dash: 'dashdot'}, opacity: 0.5, yaxis: 'y2'},
    
    {name: 'e', marker: {color: 'red'}, yaxis: 'y3'},
    {name: 'e_lower', marker: {color: 'black'}, line: {dash: 'dash'}, opacity: 0.5, yaxis: 'y3'},
    {name: 'e_upper', marker: {color: 'black'}, line: {dash: 'dash'}, opacity: 0.5, yaxis: 'y3'},
    {name: 'e_middle1', marker: {color: 'black'}, line: {dash: 'dashdot'}, opacity: 0.5, yaxis: 'y3'},
    {name: 'e_middle2', marker: {color: 'black'}, line: {dash: 'dashdot'}, opacity: 0.5, yaxis: 'y3'},

    {name: 'e_int', marker: {color: 'blue'}, yaxis: 'y4'},

    {name: 'e_diff', marker: {color: 'turquoise'}, yaxis: 'y5'},

];

const Plots = () => {
    const {t} = useTranslation();
    const controllerType = useSelector(state => state.simulator.controllerType);
    const controller = Simulator.controllers.find(controller => controller.getName() === controllerType);
    const plantName = useSelector(state => state.simulator.plant);
    const plant = Simulator.plants.find(plant => plant.getName() === plantName);

    const traces = [];
    controller.traces?.forEach(trace => {
        traces.push(tracesTemplate.find(traceElement => traceElement.name === trace));
    });

    const axisTitleTime = t('time') + (plant.unitT ? ` (${plant.unitT})` : ' (s)');
    const axisTitleControlled = t(plant.axisTitleControlled ?? 'controlled_variable') + (plant.unitY ? ` (${plant.unitY})` : '');
    const axisTitleControlledAndReference = t(plant.axisTitleControlledAndReference ?? 'controlled_and_reference_variables') + (plant.unitY ? ` (${plant.unitY})` : '');
    const axisTitleActuating = t(plant.axisTitleActuating ?? 'actuating_variable') + (plant.unitU ? ` (${plant.unitU})` : '');
    const axisTitleControlDeviation = t(plant.axisTitleControlDeviation ?? 'control_deviation') + (plant.unitE ? ` (${plant.unitE})` : '');
    const axisTitleIntDeviation = t(plant.axisTitleIntDeviation ?? 'int_control_deviation') + (plant.unitE ? ` (${plant.unitE}·${plant.unitT})` : ' ' + (plant.unitT ? ` (${plant.unitT})` : ''));
    const axisTitleDiffDeviation = t(plant.axisTitleDiffDeviation ?? 'diff_control_deviation') + ` (${plant.unitE ?? '1'}/${plant.unitT})`;

    const layout = {
        xaxis: {title: axisTitleTime, rangemode: 'nonnegative'},
        automargin: true,
        dragmode: 'pan',
        margin: {t: 20},
    };
    const subplots = [];

    if(['y', 'r'].every(trace => controller?.traces.includes(trace))){
        layout.yaxis1 = {title: axisTitleControlledAndReference};
        subplots.push('xy');
    } else if (['y'].some(trace => controller?.traces.includes(trace))) {
        layout.yaxis1 = {title: axisTitleControlled};
        subplots.push('xy');
    }
    if(['u', 'u_sat', 'u_min', 'u_max', 'u_lower', 'u_middle', 'u_upper'].some(trace => controller?.traces.includes(trace))){
        layout.yaxis2 = {title: axisTitleActuating};
        subplots.push('xy2');
    }
    if(['e', 'e_lower', 'e_upper', 'e_middle1', 'e_middle2'].some(trace => controller?.traces.includes(trace))){
        layout.yaxis3 = {title: axisTitleControlDeviation};
        subplots.push('xy3');
    }
    if(controller?.traces.includes('e_int')){
        layout.yaxis4 = {title: axisTitleIntDeviation};
        subplots.push('xy4');
    }
    if(controller?.traces.includes('e_diff')){
        if(subplots.includes('xy4')){
            layout.yaxis5 = {title: axisTitleDiffDeviation, side: 'right', overlaying: 'y4'};
        }else{
            layout.yaxis4 = {title: axisTitleDiffDeviation}
            subplots.push('xy4')
            traces.find(trace => trace.name === 'e_diff').yaxis = 'y4';
        }
    }
    layout.grid = {
        rows: subplots.length,
        columns: 1,
        ygap: 0.1,
        subplots: subplots
    }


    const data = {};

    useSelector(state => state.simulator.data).forEach(point => {
        Object.keys(point).forEach(key => {
            data[key] = (data[key] || []).concat(point[key]);
        });
    });

    traces.forEach(trace => {
        trace.y = data[trace.name] || [];
        const time = [];
        trace.y.forEach((yPoint, index) => {
            if (typeof yPoint === 'undefined') {
                time.push(undefined);
            } else {
                time.push(data.time[index]);
            }
        });
        trace.x = time;
        trace.type = 'scattergl';
        trace.mode = 'lines';
    });


    return (
        <div className={styles.plot}>
            <Plot
                id={'plot'}
                data={traces}
                layout={layout}
                config={{
                    scrollZoom: true,
                    responsive: true,
                    displaylogo: false,
                    modeBarButtonsToRemove: ['select2d', 'lasso2d', 'zoomOut2d', 'zoomIn2d', 'autoScale2d']
                }}
                style={{width: '100%', height: '100%'}}
            />
        </div>
    );

};


export default Plots;